import { useQuery } from '@tanstack/react-query';
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Sun,
  Wind,
} from 'lucide-react';

import { api } from '@/lib/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';

interface DiaClima {
  day: string;
  weatherCode: number | null;
  tempMinC: number | null;
  tempMaxC: number | null;
  precipitationMm: number | null;
  precipitationProbability: number | null;
  windMaxKmh: number | null;
  origen: 'forecast' | 'recorded';
}

/*
 * Unidades IMPERIALES al pintar, métricas al guardar.
 *
 * El producto es para EE. UU. y América, así que en pantalla van °F y mph — un
 * jefe de stand en San Diego no planifica en grados centígrados. El dato se
 * guarda en métrico porque es lo que sirve el proveedor y lo que no obliga a
 * migrar nada el día que haya un mercado que use otra cosa: la conversión es
 * de pintado, no de almacenamiento.
 */
const aF = (c: number | null) => (c == null ? null : Math.round((c * 9) / 5 + 32));
const aMph = (kmh: number | null) => (kmh == null ? null : Math.round(kmh * 0.621371));

/**
 * Icono por código WMO, agrupado por lo que le importa a quien monta un stand:
 * si va a llover, si va a hacer viento, si se ve el sol. El detalle fino («
 * llovizna helada ligera») no cambia ninguna decisión.
 */
function icono(code: number | null) {
  if (code == null) return Cloud;
  if (code === 0) return Sun;
  if (code <= 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code <= 48) return CloudFog;
  if (code <= 57) return CloudDrizzle;
  if (code <= 67) return CloudRain;
  if (code <= 77) return CloudSnow;
  if (code <= 82) return CloudRain;
  if (code <= 86) return CloudSnow;
  return CloudLightning;
}

/** La clave i18n del código, en los mismos grupos que el icono. */
function claveTiempo(code: number | null): string {
  if (code == null) return 'weather.code.unknown';
  if (code === 0) return 'weather.code.clear';
  if (code <= 2) return 'weather.code.partly';
  if (code === 3) return 'weather.code.cloudy';
  if (code <= 48) return 'weather.code.fog';
  if (code <= 57) return 'weather.code.drizzle';
  if (code <= 67) return 'weather.code.rain';
  if (code <= 77) return 'weather.code.snow';
  if (code <= 82) return 'weather.code.showers';
  if (code <= 86) return 'weather.code.snow';
  return 'weather.code.storm';
}

/**
 * El tiempo del evento: pronóstico para lo que viene, registro para lo vivido.
 *
 * La distinción se dice en pantalla y no es cosmética: un pronóstico es una
 * previsión que cambiará, y un día ya registrado es un hecho. Quien lee un
 * informe seis meses después tiene que poder distinguir «esperábamos lluvia»
 * de «llovió».
 */
/**
 * `compact`: la versión de CABECERA (sept 2026) — sólo la fila de fichas, sin
 * tarjeta, sin título y sin nota al pie, alineada a la derecha bajo el botón
 * de eliminar. Sin datos (evento sin ubicar o fuera de ventana) no pinta nada:
 * un aviso de «sin pronóstico» en la cabecera sería ruido permanente.
 */
export function EventWeatherCard({ eventId, compact = false }: { eventId: number; compact?: boolean }) {
  const t = useT();
  /*
   * El día de la semana sigue al idioma de LA APP, no al del navegador.
   *
   * Con `undefined` heredaba el del navegador: la app en inglés y las tarjetas
   * diciendo «DOM, LUN, MAR». Y las variantes son las de América —`en-US` y
   * `es-419`— que es el mercado de este producto: cambian el orden de la fecha
   * y el nombre corto del día.
   */
  const idioma = usePrefsStore((s) => s.language);
  const etiquetaFecha = idioma === 'es' ? 'es-419' : 'en-US';
  const q = useQuery({
    queryKey: ['xk-event', eventId, 'weather'],
    queryFn: () => api.get<{ dias: DiaClima[]; ubicado: boolean }>(`/xk-event/${eventId}/weather`),
    // El pronóstico cambia cada pocas horas, no cada minuto.
    staleTime: 30 * 60_000,
    retry: false,
  });

  if (q.isLoading) return compact ? <Skeleton className="h-20 w-64" /> : <Skeleton className="h-32" />;
  const dias = q.data?.dias ?? [];

  if (compact) {
    // Si el endpoint no existe en este backend (404), no hay sección que
    // enseñar. Pero con backend vivo y sin días, la sección SÍ se pinta con
    // su motivo: si no, nadie sabe que el clima vive aquí.
    if (q.isError) return null;
    if (dias.length === 0) {
      return (
        <p className="flex max-w-xs items-start gap-1.5 text-xs text-[var(--color-fg-muted)]">
          <CloudSun className="h-4 w-4 shrink-0" aria-hidden />
          {q.data?.ubicado === false ? t('weather.noLocation') : t('weather.outOfRange')}
        </p>
      );
    }
    return (
      <div className="flex max-w-full justify-end gap-1.5 overflow-x-auto pb-1 sm:max-w-[560px]">
        {dias.map((d) => {
          const Icono = icono(d.weatherCode);
          const registrado = d.origen === 'recorded';
          return (
            <div
              key={d.day}
              title={t(claveTiempo(d.weatherCode))}
              className="flex min-w-[72px] flex-none flex-col items-center gap-0.5 rounded-lg bg-[var(--color-bg-elevated)] p-2 text-center"
            >
              <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
                {new Date(`${d.day}T12:00:00`).toLocaleDateString(etiquetaFecha, {
                  weekday: 'short',
                  day: 'numeric',
                })}
              </span>
              <Icono className="h-5 w-5 text-[var(--color-accent-ink)]" aria-hidden />
              <span className="text-xs font-semibold text-[var(--color-fg-primary)]">
                {aF(d.tempMaxC) ?? '—'}°<span className="text-[var(--color-fg-muted)]">/{aF(d.tempMinC) ?? '—'}°</span>
              </span>
              {d.precipitationProbability != null ? (
                <span className="text-[9px] text-[var(--color-fg-muted)]">
                  <CloudRain className="mr-0.5 inline h-2.5 w-2.5" />
                  {d.precipitationProbability}%
                </span>
              ) : null}
              {registrado ? (
                <span className="rounded bg-[var(--color-accent-15)] px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[var(--color-accent-ink)]">
                  {t('weather.recorded')}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CloudSun className="h-5 w-5 text-[var(--color-accent-ink)]" />
          {t('weather.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {dias.length === 0 ? (
          /*
            Sin días que enseñar hay DOS motivos distintos y conviene decir
            cuál: o no sabemos dónde cae el evento, o cae fuera de la ventana
            del proveedor —más de dos semanas por delante, o ya pasado sin que
            llegáramos a registrarlo—. Un «no hay datos» a secas mandaría a
            revisar la dirección cuando el problema es la fecha.
          */
          <p className="rounded-lg border border-dashed border-[var(--color-border-default)] p-6 text-center text-sm text-[var(--color-fg-muted)]">
            {q.data?.ubicado === false ? t('weather.noLocation') : t('weather.outOfRange')}
          </p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {dias.map((d) => {
              const Icono = icono(d.weatherCode);
              const registrado = d.origen === 'recorded';
              return (
                <div
                  key={d.day}
                  className="flex min-w-[104px] flex-none flex-col items-center gap-1 rounded-xl bg-[var(--color-bg-elevated)] p-3 text-center shadow-[var(--shadow-card)]"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
                    {new Date(`${d.day}T12:00:00`).toLocaleDateString(etiquetaFecha, {
                      weekday: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <Icono className="h-6 w-6 text-[var(--color-accent-ink)]" aria-hidden />
                  <span className="text-sm font-semibold text-[var(--color-fg-primary)]">
                    {aF(d.tempMaxC) ?? '—'}° <span className="text-[var(--color-fg-muted)]">/ {aF(d.tempMinC) ?? '—'}°</span>
                  </span>
                  <span className="text-[10px] text-[var(--color-fg-muted)]">
                    {t(claveTiempo(d.weatherCode))}
                  </span>
                  {d.precipitationProbability != null ? (
                    <span className="text-[10px] text-[var(--color-fg-muted)]">
                      <CloudRain className="mr-0.5 inline h-3 w-3" />
                      {d.precipitationProbability}%
                    </span>
                  ) : null}
                  {d.windMaxKmh != null ? (
                    <span className="text-[10px] text-[var(--color-fg-muted)]">
                      <Wind className="mr-0.5 inline h-3 w-3" />
                      {aMph(d.windMaxKmh)} mph
                    </span>
                  ) : null}
                  {/* Hecho o previsión: la etiqueta sólo va en lo registrado —
                      marcar los dos lados sería ruido en la mitad de las
                      tarjetas. */}
                  {registrado ? (
                    <span className="rounded bg-[var(--color-accent-15)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--color-accent-ink)]">
                      {t('weather.recorded')}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-[10px] leading-snug text-[var(--color-fg-muted)]">
          {t('weather.footnote')}
        </p>
      </CardContent>
    </Card>
  );
}
